import { useEffect, useMemo, useRef } from 'react';
import { Range as RangeComponent, getTrackBackground, useThumbOverlap } from 'react-range';
import styled from 'styled-components';

import { isNum } from '@utils/helpers';

const Wrapper = styled.div`
	min-width: fit-content;
	width: 100%;
`;
const RangeWrapper = styled.div`
	padding-top: ${({ $isSmall }) => ($isSmall ? '4px' : '31px')};
	padding-bottom: 4px;
	padding-inline: 5px;
`;

const Track = styled.div`
	width: 100%;
	height: 12px;
	border-radius: ${({ $brl, $brr }) => `${$brl} ${$brr} ${$brr} ${$brl}`};
	cursor: grab;

	transition: height 0.2s ease-in-out;

	/* TODO: Maybe increase height on track hover? */
	&:active {
		cursor: grabbing;
	}
`;

const ThumbWrapper = styled.div`
	width: 10px;
	height: 19px;
	border-radius: 100vw;

	position: relative;
	outline: none;

	&:hover,
	&:focus-visible,
	&:active {
		& > .range-thumb {
			outline: 1px solid #222222;
			width: 6px;
			height: 17px;
			border-radius: 100vw;
		}
	}
`;

const Thumb = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	translate: -50% -50%;

	width: 4px;
	height: 14px;
	border-radius: 4px;

	background: white;
	transition: box-shadow 0.2s;
	box-shadow:
		0 0 5px 0px rgba(0, 0, 0, 0.5),
		0 0 5px 0px rgba(0, 0, 0, 0.5);
`;

const StyledThumbLabel = styled.div`
	position: absolute;
	bottom: 100%;
	left: 50%;
	translate: -50% -4px;
	pointer-events: none;

	background: #00000088;
	color: white;
	font-size: 0.725rem;
	font-weight: 500;
	padding: 2px 6px;
	border-radius: 4px;
	white-space: nowrap;
`;

const MinMaxLabels = styled.div`
	display: flex;
	justify-content: space-between;
	font-size: 0.75rem;
	color: #aaa;
	margin-top: 2px;
	padding-inline: 4.5px;

	/* Center label on thumb but will not grow outside Wrapper width */
	& :first-child {
		transform: translateX(max(-50%, -15px));
	}
	& :last-child {
		transform: translateX(min(50%, 15px));
	}
`;

const ThumbLabel = ({ rangeRef, values, index, step, combinator = ' - ', displayValue }) => {
	const [value, style] = useThumbOverlap(
		rangeRef.current,
		values,
		index,
		step,
		combinator,
		displayValue
	);
	delete style.transform;
	return <StyledThumbLabel style={style}>{value}</StyledThumbLabel>;
};

const interpolate = (start, end, factor) => start + (end - start) * factor;
const normalize = (value, min, max) => {
	const denom = max - min;
	if (denom === 0) return 0;
	return (value - min) / denom;
};
const getMinMax = valueArr => {
	let [minVal, maxVal] = [Infinity, -Infinity];
	for (const val of valueArr) {
		if (val < minVal) minVal = val;
		if (val > maxVal) maxVal = val;
	}

	return [minVal, maxVal];
};

// TODO: Potentially fix screen reader accessibility? (labelledBy)
const Range = ({
	label: _label, // kept out of ...props; the slider uses its own aria label below
	step = 1,
	min = 0,
	max = 10,
	values = [0, 10],
	onChange = values => console.log({ values }),
	onFinalChange = values => console.log({ values }),
	displayValue = val => val,
	trackColors = ['#343a40', '#20AC6B', '#343a40'],
	hideMinMaxLabels = false,
	hideThumbLabels = false,
	radiusConfig = { start: 0, end: 0.03, radiusStart: 0, radiusEnd: 6 },
	...props
}) => {
	const rangeRef = useRef(null);
	const background = getTrackBackground({ values, colors: trackColors, min, max });

	// Validation: Falsy = valid, String = error message
	const errorMessage = useMemo(() => {
		const range = max - min;
		if (!isNum(range) || !isFinite(range) || range <= 0)
			return `Invalid range: ${range}. min=${min}, max=${max}`;
		if (!isNum(step) || !isFinite(step) || step <= 0)
			return `Invalid step: ${step}. Step must be a positive number/decimal.`;

		return null;
	}, [min, max, step]);

	useEffect(() => {
		if (errorMessage) console.error(errorMessage);
	}, [errorMessage]);

	if (errorMessage) {
		return null;
	}
	return (
		<Wrapper {...props}>
			<RangeWrapper $isSmall={hideThumbLabels}>
				<RangeComponent
					ref={rangeRef}
					label="Range Slider"
					// labelledBy={}
					step={step}
					min={min}
					max={max}
					values={values}
					onFinalChange={onFinalChange}
					onChange={onChange}
					renderTrack={({ props, children }) => {
						// Thumb values
						const [leftThumb, rightThumb] = getMinMax(values).map(v =>
							normalize(v, min, max)
						);

						// Border Radius Config
						const { start, end, radiusStart, radiusEnd } = radiusConfig;

						// Distance from edges
						const distFromLeft = Math.abs(0 - leftThumb);
						const distFromRight = Math.abs(1 - rightThumb);

						const leftFactor = normalize(distFromLeft, start, end);
						const rightFactor = normalize(distFromRight, start, end);

						let [leftValue, rightValue] = [radiusEnd, radiusEnd];
						if (distFromLeft < end)
							leftValue = interpolate(radiusStart, radiusEnd, leftFactor);
						if (distFromRight < end)
							rightValue = interpolate(radiusStart, radiusEnd, rightFactor);

						return (
							<Track
								{...props}
								style={{ background }}
								$brl={leftValue + 'px'}
								$brr={rightValue + 'px'}>
								{children}
							</Track>
						);
					}}
					renderThumb={({ props, index }) => (
						<ThumbWrapper {...props} key={props.key}>
							<Thumb className="range-thumb" />
							{!hideThumbLabels && (
								<ThumbLabel
									rangeRef={rangeRef}
									values={values}
									index={index}
									step={step}
									displayValue={displayValue}
								/>
							)}
						</ThumbWrapper>
					)}
				/>
			</RangeWrapper>
			{!hideMinMaxLabels && (
				<MinMaxLabels>
					<div>{displayValue(min)}</div>
					<div>{displayValue(max)}</div>
				</MinMaxLabels>
			)}
		</Wrapper>
	);
};

export default Range;
